import supabase from '../supabase/supabase';

/**
 * Dependency Service
 * Checks what will be affected when deleting various entities
 */

/**
 * Check dependencies for a case
 * @param {string} caseId - Case ID
 * @returns {object} Dependencies object
 */
export const checkCaseDependencies = async (caseId) => {
  try {
    const dependencies = {};

    // Get case stages
    const { data: stages, error: stagesError } = await supabase
      .from('case_stages')
      .select('id, stage_name')
      .eq('case_id', caseId)
      .is('deleted_at', null);

    if (stagesError) throw stagesError;
    dependencies['Case Stages'] = stages?.length || 0;

    // Get case actions
    const { data: actions, error: actionsError } = await supabase
      .from('case_actions')
      .select('id, action_name, stage_id')
      .in('stage_id', stages?.map(s => s.id) || []);

    if (actionsError) throw actionsError;
    dependencies['Case Actions'] = actions?.length || 0;

    // Get case documents
    const { data: documents, error: docsError } = await supabase
      .from('case_documents')
      .select('id, document_name')
      .eq('case_id', caseId);

    if (docsError) throw docsError;
    dependencies['Documents'] = documents?.map(d => d.document_name) || [];

    // Get case notes
    const { data: notes, error: notesError } = await supabase
      .from('case_notes')
      .select('id, note_title')
      .eq('case_id', caseId);

    if (notesError) throw notesError;
    dependencies['Notes'] = notes?.length || 0;

    // Get audit log entries
    const { data: auditLogs, error: auditError } = await supabase
      .from('case_audit_log')
      .select('id')
      .eq('case_id', caseId);

    if (auditError) throw auditError;
    dependencies['Audit Log Entries'] = auditLogs?.length || 0;

    return dependencies;
  } catch (error) {
    console.error('Error checking case dependencies:', error);
    return {};
  }
};

/**
 * Check dependencies for a staff member
 * @param {string} staffId - Staff member ID
 * @returns {object} Dependencies object
 */
export const checkStaffDependencies = async (staffId) => {
  try {
    const dependencies = {};

    // Get assigned cases - this is the primary dependency for staff members
    const { data: cases, error: casesError } = await supabase
      .from('cases')
      .select('id, case_id, case_type, status')
      .eq('staff_id', staffId)
      .is('deleted_at', null);

    if (casesError) throw casesError;
    dependencies['Assigned Cases'] = cases?.map(c => `${c.case_id} (${c.case_type}) - ${c.status}`) || [];

    // Note: Workflows are company-wide HR process templates, not staff-specific records
    // They are not checked here as they are not owned by individual staff members

    return dependencies;
  } catch (error) {
    console.error('Error checking staff dependencies:', error);
    return {};
  }
};

/**
 * Check dependencies for a workflow
 * @param {string} workflowId - Workflow ID
 * @returns {object} Dependencies object
 */
export const checkWorkflowDependencies = async (workflowId) => {
  try {
    const dependencies = {};

    // Get workflow steps
    const { data: steps, error: stepsError } = await supabase
      .from('workflow_steps')
      .select('id, step_name')
      .eq('workflow_id', workflowId)
      .is('deleted_at', null);

    if (stepsError) throw stepsError;
    dependencies['Workflow Steps'] = steps?.length || 0;

    // Get workflow notes
    const { data: notes, error: notesError } = await supabase
      .from('workflow_notes')
      .select('id, note_title')
      .eq('workflow_id', workflowId);

    if (notesError) throw notesError;
    dependencies['Notes'] = notes?.length || 0;

    // Get audit log entries
    const { data: auditLogs, error: auditError } = await supabase
      .from('workflow_audit_log')
      .select('id')
      .eq('workflow_id', workflowId);

    if (auditError) throw auditError;
    dependencies['Audit Log Entries'] = auditLogs?.length || 0;

    return dependencies;
  } catch (error) {
    console.error('Error checking workflow dependencies:', error);
    return {};
  }
};

/**
 * Check dependencies for a team member (tenant_members)
 * @param {string} memberId - Member ID (tenant_members.id)
 * @returns {object} Dependencies object
 */
export const checkTeamDependencies = async (memberId) => {
  try {
    const dependencies = {};

    // Note: tenant_members doesn't have direct dependencies
    // The member can be safely removed without cascading effects
    // This function is kept for UI consistency
    
    return dependencies;
  } catch (error) {
    console.error('Error checking team dependencies:', error);
    return {};
  }
};

/**
 * Check dependencies for a document
 * @param {string} documentId - Document ID
 * @param {string} documentType - Type of document ('case' or 'staff')
 * @returns {object} Dependencies object
 */
export const checkDocumentDependencies = async (documentId, documentType = 'case') => {
  try {
    const dependencies = {};

    if (documentType === 'case') {
      // Check if document is linked to case actions
      const { data: actions, error: actionsError } = await supabase
        .from('case_actions')
        .select('id, action_name')
        .contains('document_ids', [documentId]);

      if (actionsError) throw actionsError;
      dependencies['Linked to Case Actions'] = actions?.length || 0;
    }

    // File will be deleted from storage
    dependencies['Storage Files'] = 1;

    return dependencies;
  } catch (error) {
    console.error('Error checking document dependencies:', error);
    return {};
  }
};

/**
 * Generic dependency checker
 * Routes to appropriate checker based on entity type
 * @param {string} entityType - Type of entity ('case', 'staff', 'workflow', 'team', 'document')
 * @param {string} entityId - Entity ID
 * @param {object} options - Additional options (e.g., documentType for documents)
 * @returns {object} Dependencies object
 */
export const checkDependencies = async (entityType, entityId, options = {}) => {
  switch (entityType.toLowerCase()) {
    case 'case':
      return await checkCaseDependencies(entityId);
    case 'staff':
    case 'staff member':
      return await checkStaffDependencies(entityId);
    case 'workflow':
      return await checkWorkflowDependencies(entityId);
    case 'team':
      return await checkTeamDependencies(entityId);
    case 'document':
      return await checkDocumentDependencies(entityId, options.documentType);
    default:
      console.warn(`Unknown entity type: ${entityType}`);
      return {};
  }
};
